import CommonList from "@/components/common/List";
import CommonTableList from "@/components/common/TableList";
import { Checkbox } from "@/components/ui/checkbox";
import { ColumnDef } from "@tanstack/react-table";
import React, { FormEvent, useState } from "react";
import InvoiceCreate from "./Create";
import InvoiceDetail from "./InvoiceDetails";
import DialogConfirmCommon from "@/components/common/DialogConfirm";
import DatePicker from "@/components/input/DatePicker";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { handleFormChange } from "@/utils/formChange";
import { ResourceContextProvider } from "react-admin";
import { Invoice } from "@/interface/entityInterface";
import { format, subYears } from "date-fns";
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { MoreVerticalIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import CommonDelete from "@/components/common/Delete";

const InvoiceList = () => {
  const [open, setOpen] = useState(false);
  const [row, setRow] = useState(null);
  const [props, setProps] = useState({
    date: new Date(),
    status: "Both",
  });

  const handleRowClick = (e: any, data: Invoice) => {
    if (e.target.role == "checkbox") {
      e.stopPropagation();
      return;
    }
    setOpen(true);
    setRow(data);
  };

  const columns = React.useMemo<ColumnDef<Invoice>[]>(
    () => [
      {
        id: "select",
        header: ({ table }) => (
          <Checkbox
            checked={
              table.getIsAllPageRowsSelected() ||
              (table.getIsSomePageRowsSelected() && "indeterminate")
            }
            onCheckedChange={(value) =>
              table.toggleAllPageRowsSelected(!!value)
            }
            aria-label="Select all"
          />
        ),
        cell: ({ row }) => (
          <Checkbox
            checked={row.getIsSelected()}
            onCheckedChange={(value) => row.toggleSelected(!!value)}
            aria-label="Select row"
          />
        ),
        enableSorting: false,
        enableHiding: false,
      },
      {
        accessorKey: "id",
        header: "ID",
        cell: ({ row }) => (
          <div className="capitalize">{row.getValue("id")}</div>
        ),
      },
      {
        accessorFn: (row) => row.guest_user.name || "",
        id: "client",
        header: "Client",
        filterFn: "includesString",
        cell: ({ row }) => {
          return (
            <div className="flex flex-col text-sm space-y-1">
              <span className="font-medium">
                {row.original?.guest_user?.name}
              </span>
            </div>
          );
        },
      },
      {
        accessorKey: "vehicleRef",
        header: "Vehicle",
        cell: ({ row }) => {
          const vehicle = row.original.guest_vehicle;
          return (
            <div className="flex flex-col text-sm space-y-1">
              <span className="font-medium">{vehicle?.license_plate}</span>
            </div>
          );
        },
      },
      {
        accessorKey: "date",
        header: "Date",
        cell: ({ row }) => {
          return (
            <div className="flex flex-col text-sm space-y-1">
              <span className="font-medium">{row.getValue("date") ? format(row.getValue("date"), "dd MMMM yyyy HH:mm") : "-"}</span>
            </div>
          );
        },
      },
      {
        accessorKey: "total",
        header: "Total",
        cell: ({ row }) => {
          return (
            <div className="flex flex-col text-sm space-y-1">
              <span className="font-medium">{row.getValue("total")}</span>
            </div>
          );
        },
      },
      {
        accessorKey: "status",
        header: "Status ",
        cell: ({ row }) => {
          return (
            <DialogConfirmCommon
              id={row.original.id}
              endpoint="auto-service-invoices"
              endpoint2="updateStatus"
              selectedValue={row.original.status}
              dataOptions={["Paid", "Unpaid"]}
              description="Are you sure you want to update the status"
              propKey="status"
            />
          );
        },
      },
      {
        id: "actions",
        cell: ({ row }) => (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                className="flex size-8 text-muted-foreground data-[state=open]:bg-muted"
                size="icon"
              >
                <MoreVerticalIcon />
                <span className="sr-only">Open menu</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-32">
              {/* <DropdownMenuItem>Edit</DropdownMenuItem>
              <DropdownMenuSeparator /> */}
              <CommonDelete id={row.original.id} resource="auto-service-invoices" />
            </DropdownMenuContent>
          </DropdownMenu>
        ),
      },
    ],
    [],
  );

  return (
    <ResourceContextProvider value="auto-service-invoices">
              <div className="w-full max-w-md">
          <DatePicker
            value={props.date}
            setValue={(e: FormEvent<any>) => handleFormChange(e, setProps)}
            minDate={subYears(new Date(), 1)}
          />
        </div>
        <div className="w-full max-w-md mt-6">
          <Tabs
            className="w-full"
            value={props.status}
            onValueChange={(val: any) => {
              const fakeEvent = {
                currentTarget: {
                  id: "status",
                  value: val,
                },
              };

              handleFormChange(fakeEvent as any, setProps);
            }}
          >
            <TabsList className="w-full">
              <TabsTrigger value="Both">Both</TabsTrigger>
              <TabsTrigger value="Paid">Paid</TabsTrigger>
              <TabsTrigger value="Unpaid">Not Paid</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
      <CommonList
        filters={
          props.status === "Both"
            ? {
                date: props.date,
              }
            : props
        }
      >
        <CommonTableList
          columns={columns}
          filterSearch="client  "
          create={<InvoiceCreate />}
          rowClick={handleRowClick}
        />
        {open && <InvoiceDetail open={open} setOpen={setOpen} invoice={row} />}
      </CommonList>
    </ResourceContextProvider>
  );
};

export default InvoiceList;
